import React, { useEffect, useState } from 'react';
import './App.css';

// ── Typewriter hook ──────────────────────────────────────
const ROLES = [
  'Platform Engineer',
  'AI Infrastructure Engineer',
  'Product-minded Engineer',
  'MLOps Engineer',
];

function useTypewriter(words: string[], speed = 80, pause = 1800) {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplay(current.slice(0, charIdx + 1));
        if (charIdx + 1 === current.length) {
          setTimeout(() => setDeleting(true), pause);
        } else {
          setCharIdx(c => c + 1);
        }
      } else {
        setDisplay(current.slice(0, charIdx - 1));
        if (charIdx - 1 === 0) {
          setDeleting(false);
          setWordIdx(i => (i + 1) % words.length);
          setCharIdx(0);
        } else {
          setCharIdx(c => c - 1);
        }
      }
    }, deleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return display;
}

// ── Data ─────────────────────────────────────────────────
const VALUE_PROPS = [
  {
    icon: '⚙️',
    title: 'AI infrastructure',
    desc: 'Infrastructure for AI and ML systems that need reliability, observability, and operational discipline.',
  },
  {
    icon: '🧠',
    title: 'Operational tooling',
    desc: 'Automation, cluster operations, delivery workflows, and platform tooling for teams running complex systems.',
  },
  {
    icon: '🤖',
    title: 'Model integration',
    desc: 'Model serving, MCP, agents, and RAG where models need to connect cleanly to tools and infrastructure.',
  },
  {
    icon: '🧭',
    title: 'Product sense',
    desc: 'I like work where system design, developer workflows, constraints, and user value have to align.',
  },
];

const COLLABORATION = [
  {
    icon: '🧩',
    title: 'Operating AI workloads',
    desc: 'Deployment, model serving, operations, and platform work for AI systems that have to run reliably.',
  },
  {
    icon: '🚀',
    title: 'Model serving and reliability',
    desc: 'Serving patterns, rollout safety, observability, and runtime reliability for model-backed systems.',
  },
  {
    icon: '🔌',
    title: 'Tool-connected models',
    desc: 'MCP and agent workflows that connect models to tools, systems, and developer environments.',
  },
  {
    icon: '📊',
    title: 'Retrieval and evaluation',
    desc: 'RAG pipelines, evaluation loops, observability, and the infrastructure behind retrieval-based systems.',
  },
  {
    icon: '🧠',
    title: 'Technical product work',
    desc: 'Developer-facing and platform-facing product problems where technical depth, prioritization, and execution all matter.',
  },
];

const LEARNING = [
  {
    icon: '🛰️',
    title: 'AI Infrastructure',
    label: 'active',
    desc: 'Platform and operational patterns for AI workloads, model serving, and system reliability.',
  },
  {
    icon: '🔌',
    title: 'MCP',
    label: 'in progress',
    desc: 'Connecting models to infrastructure and engineering workflows.',
  },
  {
    icon: '🤖',
    title: 'AI Agents',
    label: 'in progress',
    desc: 'Tool use and orchestration for agents that need real system access.',
  },
  {
    icon: '📚',
    title: 'RAG',
    label: 'in progress',
    desc: 'Retrieval pipelines, evaluation, and the engineering behind useful retrieval.',
  },
  {
    icon: '📡',
    title: 'Observability',
    label: 'active',
    desc: 'Tracing, metrics, and system feedback for AI components that need to be debugged and operated.',
  },
];

const SIGNALS = [
  { label: 'Platform', value: 'online' },
  { label: 'Inference', value: 'steady' },
  { label: 'Tooling', value: 'active' },
];

// ── Icons ─────────────────────────────────────────────────
function IconGitHub() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.373 0 12c0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.298 24 12c0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

function IconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconCredly() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HeroVisual() {
  return (
    <div className="hero-visual" aria-hidden="true">
      <div className="hero-shards">
        <span className="hero-shard hero-shard-a" />
        <span className="hero-shard hero-shard-b" />
        <span className="hero-shard hero-shard-c" />
      </div>
      <div className="hero-panel">
        <div className="hero-panel-header">
          <span className="hero-panel-title">ai-infra/control-plane</span>
          <span className="hero-panel-status">running</span>
        </div>

        <div className="signal-grid">
          {SIGNALS.map(signal => (
            <div className="signal-card" key={signal.label}>
              <span className="signal-label">{signal.label}</span>
              <span className="signal-value">{signal.value}</span>
            </div>
          ))}
        </div>

        <div className="flow-map">
          <div className="flow-node">Models</div>
          <div className="flow-link" />
          <div className="flow-node">Tools</div>
          <div className="flow-link" />
          <div className="flow-node">Infra</div>
        </div>

        <div className="orbit-diagram">
          <div className="orbit-core" />
          <div className="orbit-ring orbit-ring-a" />
          <div className="orbit-ring orbit-ring-b" />
          <div className="orbit-ring orbit-ring-c" />
          <div className="orbit-point orbit-point-a" />
          <div className="orbit-point orbit-point-b" />
          <div className="orbit-point orbit-point-c" />
        </div>

        <div className="metric-stack">
          <div className="metric-row">
            <span>MCP endpoints</span>
            <div className="metric-bar"><span className="metric-fill metric-fill-a" /></div>
          </div>
          <div className="metric-row">
            <span>Agent loops</span>
            <div className="metric-bar"><span className="metric-fill metric-fill-b" /></div>
          </div>
          <div className="metric-row">
            <span>Retrieval paths</span>
            <div className="metric-bar"><span className="metric-fill metric-fill-c" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────
function App() {
  const role = useTypewriter(ROLES);

  return (
    <div className="app">
      {/* Nav */}
      <nav className="nav">
        <span className="nav-logo">~/pablo-felix</span>
        <ul className="nav-links">
          <li><a href="#value">What I do</a></li>
          <li><a href="#collab">Collaborate</a></li>
          <li><a href="#learning">Focus</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>
      </nav>

      {/* Hero */}
      <section className="hero">
        <p className="hero-eyebrow">Platform Engineering · AI Infrastructure · Madrid</p>
        <h1>Pablo Félix<br />Estévez Pico</h1>
        <div className="hero-role">
          <span className="hero-role-prefix">$&gt;</span>
          <span className="hero-role-text">{role}</span>
          <span className="cursor" aria-hidden="true" />
        </div>
        <p className="hero-sub">
          I build infrastructure and tooling for AI systems that need to run reliably.
          My focus is <strong>platform engineering</strong>, <strong>automation</strong>, and
          <strong> AI infrastructure</strong>, especially around <strong>MCP</strong>, <strong>agents</strong>,
          <strong>RAG</strong>, <strong>model serving</strong>, and <strong>observability</strong>.
          I am also interested in <strong>technical product work</strong> around AI platforms, developer tooling, and systems that need clear trade-offs.
        </p>
        <div className="hero-ctas">
          <a className="btn btn-primary" href="#collab">Collaboration areas</a>
          <a className="btn btn-outline" href="https://www.linkedin.com/in/pablofep/" target="_blank" rel="noopener noreferrer">
            <IconLinkedIn /> LinkedIn
          </a>
          <a className="btn btn-outline" href="#contact">Contact</a>
        </div>
        <HeroVisual />
      </section>

      {/* Value Props */}
      <section id="value" className="section">
        <p className="section-label">// what_i_do</p>
        <h2>What I do</h2>
        <p className="section-desc">
          The part of the stack where infrastructure, engineering workflows, product constraints, and applied AI meet.
        </p>
        <div className="value-grid">
          {VALUE_PROPS.map(v => (
            <div className="value-card" key={v.title}>
              <div className="value-icon">{v.icon}</div>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Collaboration */}
      <section id="collab" className="section">
        <p className="section-label">// collaboration</p>
        <h2>Open to collaboration in</h2>
        <p className="section-desc">
          The technical and product-facing problems I am most interested in working on.
        </p>
        <div className="value-grid">
          {COLLABORATION.map(item => (
            <div className="value-card" key={item.title}>
              <div className="value-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Currently Learning */}
      <section id="learning" className="section">
        <p className="section-label">// currently_building</p>
        <h2>Current focus</h2>
        <p className="section-desc">
          Current work where model behavior meets infrastructure, tooling, observability, and operational constraints.
        </p>
        <div className="learning-grid">
          {LEARNING.map(item => (
            <div className="learning-card" key={item.title}>
              <div className="learning-card-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <span className="tag-mini">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section">
        <div className="contact-section">
          <h2>Collaboration opportunities</h2>
          <p>
            Open to collaboration on AI infrastructure, platform tooling, model serving, MCP integrations, agent workflows, evaluation, observability, retrieval systems, and technical product work for developer-facing AI platforms.
          </p>
          <div className="contact-links">
            <a className="contact-link" href="https://www.linkedin.com/in/pablofep/" target="_blank" rel="noopener noreferrer">
              <IconLinkedIn /> LinkedIn
            </a>
            <a className="contact-link" href="https://github.com/pablofelix" target="_blank" rel="noopener noreferrer">
              <IconGitHub /> GitHub
            </a>
            <a className="contact-link" href="https://www.credly.com/users/pablo-felix-estevez-pico" target="_blank" rel="noopener noreferrer">
              <IconCredly /> Credly
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        Pablo Félix Estévez Pico · Madrid, Spain · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

export default App;
